import _ from 'lodash';
import {connect} from 'react-redux';

import {HandlerOf, PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {CellsConversationsDragItem} from '../../../../helpers/conversationCellHelpers';
import {buildTeammateInboxLabelSectionExpandedKey} from '../../../../helpers/expansionTogglingHelpers';
import {wrapActionCreator} from '../../../../helpers/reduxHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../services/tracking';
import {Dispatch} from '../../../../store/appStore';
import {getHasInternalDiscussions} from '../../../../store/data/company/companySelectors';
import {getUserOwnedInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {makeGetCountersVisibility} from '../../../../store/data/preferences/countersSelectors';
import {
  canDropOnTeammateInboxTagAction,
  moveToTeammateInboxTagAction,
} from '../../../../store/data/teammates/teammatesActions';
import {getUser, getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {reorderNestedFolderInPersonalWorkspaceAsyncAction} from '../../../../store/data/workspace/workspaceActions';
import {getShouldSimplifyPersonalWorkspace} from '../../../../store/stateless/cellsEmptySetupActionsSelectors';
import {canDropWorkspaceItemAtTarget} from '../dnd/workspaceDragDropHelpers';
import {WorkspaceDragSourceTypesEnum, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {TeammateWorkspaceInboxTag} from './teammateWorkspaceInboxTag';

/*
 * Constants.
 */

const defaultIsInboxTagSectionExpanded = true;

/*
 * Selectors.
 */

/** Find whether this section should be expanded or not. */
const getIsExpanded = createSelector([getUserNamespace, getFindIsExpanded], (userNamespace, findIsExpanded) =>
  findIsExpanded(buildTeammateInboxLabelSectionExpandedKey(userNamespace), defaultIsInboxTagSectionExpanded),
);

/** Find the counters visibility for the teammate's workspace. */
const getCountersVisibility = makeGetCountersVisibility(getUserNamespace);

const getUserOwnerInboxesMap = createSelector([getUserOwnedInboxes], (inboxes) =>
  _.keyBy(inboxes, (i) => i.id),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  user: getUser,
  inboxes: getUserOwnerInboxesMap,
  countersVisibility: getCountersVisibility,
  isExpanded: getIsExpanded,
  hasInternalDiscussions: getHasInternalDiscussions,
  shouldHideToggleExpand: getShouldSimplifyPersonalWorkspace,
}));

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    toggleExpandItemAction: wrapActionCreator(dispatch, toggleExpandItemAction),
    canDropOnTeammateInboxTagAction: wrapActionCreator(dispatch, canDropOnTeammateInboxTagAction),
    moveToTeammateInboxTagAction: wrapActionCreator(dispatch, moveToTeammateInboxTagAction),
    reorderWorkspacePersonalFolder: wrapActionCreator(
      dispatch,
      reorderNestedFolderInPersonalWorkspaceAsyncAction,
    ),
  };
}

export const TeammateWorkspaceInboxTagContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {user} = stateProps;

    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.INBOX,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItemAction(
        buildTeammateInboxLabelSectionExpandedKey(user.namespace),
        defaultIsInboxTagSectionExpanded,
      );
    };

    const canConversationsDrop: PredicateOf<CellsConversationsDragItem> = (options) =>
      dispatchProps.canDropOnTeammateInboxTagAction(options, user);

    const onConversationsDrop: HandlerOf<CellsConversationsDragItem> = (options) =>
      dispatchProps.moveToTeammateInboxTagAction(options, user);

    const onWorkspaceItemDrop: WorkspaceItemDropHandler = (droppedItem, linePosition, target) => {
      if (!target || !canDropWorkspaceItemAtTarget(droppedItem, target)) {
        return;
      }

      if (
        droppedItem.type !== WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER ||
        target.type !== WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER
      ) {
        return;
      }

      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_REORDER, {
        section: WorkspaceDisplaySectionTrackingTypesEnum.INDIV_SUB_INBOX,
      });
      dispatchProps.reorderWorkspacePersonalFolder({
        droppedFolder: droppedItem,
        targetFolder: target,
        linePosition,
      });
    };

    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick: stateProps.shouldHideToggleExpand ? undefined : onToggleExpandClick,
      canConversationsDrop,
      onConversationsDrop,
      onWorkspaceItemDrop,
    };
  },
)(TeammateWorkspaceInboxTag);
